import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM.js";
import XYZ from "ol/source/XYZ.js";
import { fromLonLat } from "ol/proj.js";
import { defaults as defaultControls } from "ol/control.js";
import VectorTileLayer from "ol/layer/VectorTile.js";
import VectorTileSource from "ol/source/VectorTile.js";
import MVT from "ol/format/MVT.js";
import { stylefunction } from "ol-mapbox-style";
import TileWMS from 'ol/source/TileWMS.js';
import ImageArcGISRest from 'ol/source/ImageArcGISRest.js';
import ImageLayer from 'ol/layer/Image.js';

class MapService {
  constructor() {
    this.map = null;
    this.baseLayer = null;
    this._baseOverlay = null;

    // Configuración inicial del mapa
    this.initialCenter = fromLonLat([-70.4, -33.27]);
    this.initialZoom = 3.5;

    this.baseLayersCatalog = {
      osm: () => new OSM(),
      satellite: () =>
        new XYZ({
          url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        }),
      terrain: () =>
        new XYZ({
          url: "https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
        }),
      dark: () =>
        new XYZ({
          url: "https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        }),
    };
  }

  /**
   * Inicializa el mapa si no existe ya.
   */
  init(target = "map") {
    if (this.map) return this.map;

    const container = document.getElementById(target);
    if (!container) {
      console.error(`[MapService] No se encontró el contenedor #${target}`);
      return null;
    }

    // Crear capa base inicial
    this.baseLayer = new TileLayer({
      source: new OSM(),
    });

    this.map = new Map({
      target,
      layers: [this.baseLayer],
      view: new View({
        center: this.initialCenter,
        zoom: this.initialZoom,
      }),
      controls: defaultControls({
        zoom: false,
        rotate: false,
        attribution: true,
      }),
    });

    return this.map;
  }

  /**
   * Retorna el mapa actual
   */
  get() {
    return this.map;
  }

  /**
   * Cambia la capa base dinámicamente.
   * On tile/style load failure: alert + rollback to previous base layer.
   */
  changeBaseMap(modeOrConfig) {
    if (!this.map) return;

    const previousLayer = this.baseLayer;
    const previousOverlay = this._baseOverlay;
    let newLayer = null;

    try {
      if (typeof modeOrConfig === 'string') {
        const sourceFactory = this.baseLayersCatalog[modeOrConfig];
        if (!sourceFactory) {
          console.warn(`[MapService] Basemap ID "${modeOrConfig}" no encontrado en catálogo.`);
          return;
        }
        newLayer = new TileLayer({ source: sourceFactory(), zIndex: 0 });
      } else if (modeOrConfig && typeof modeOrConfig === 'object') {
        newLayer = this._buildBaseLayerFromConfig(modeOrConfig, previousLayer);
      }
    } catch (err) {
      console.error('[MapService] Error construyendo capa base:', err);
      alert('Error cargando mapa base. Manteniendo mapa anterior.');
      return;
    }

    if (!newLayer) {
      console.warn('[MapService] No se pudo crear la capa para configuración:', modeOrConfig);
      return;
    }

    if (newLayer instanceof TileLayer) {
      this._attachTileErrorRollback(newLayer, previousLayer, previousOverlay);
    }

    this._swapBaseLayer(newLayer);
    this._applyOverlay(modeOrConfig);
    console.log('[MapService] Capa base cambiada correctamente.');
  }

  _buildBaseLayerFromConfig(config, previousLayer) {
    const isVector = config.type === 'vector-tile' || config.url?.includes('VectorTileServer');
    if (isVector) return this._buildVectorTileLayer(config, previousLayer);
    if (config.type === 'osm' || config.provider === 'OSM') {
      return new TileLayer({ source: new OSM(), zIndex: 0 });
    }
    if (config.url) {
      let url = config.url;
      if (!url.includes('{x}') && !url.includes('{z}')) {
        url = `${url}/tile/{z}/{y}/{x}`;
      }
      console.log(`[MapService] Aplicando basemap XYZ: ${url}`);
      return new TileLayer({
        source: new XYZ({ url, attributions: config.attributions }),
        zIndex: 0,
      });
    }
    return null;
  }

  _buildVectorTileLayer(config, previousLayer) {
    console.log(`[MapService] Aplicando basemap vectorial: ${config.styleUrl || config.url}`);
    let url = config.url || '';
    if (url.includes('VectorTileServer') && !url.includes('{z}')) {
      url = `${url}/tile/{z}/{y}/{x}.pbf`;
    }
    const source = new VectorTileSource({
      format: new MVT(),
      url,
      attributions: config.attributions || '© Esri, OpenStreetMap contributors',
    });
    const layer = new VectorTileLayer({ source, zIndex: 0 });

    const styleUrl = config.styleUrl || (url.includes('arcgis')
      ? 'https://cdn.arcgis.com/sharing/rest/content/items/4a7116253de34cd59511c8abc2cc3ac5/resources/styles/root.json'
      : null);

    if (styleUrl) {
      fetch(styleUrl)
        .then((r) => {
          if (!r.ok) throw new Error(`Style fetch ${r.status}`);
          return r.json();
        })
        .then((glStyle) => {
          const sourceKey = glStyle.sources ? Object.keys(glStyle.sources)[0] : 'esri';
          stylefunction(layer, glStyle, sourceKey);
        })
        .catch((err) => {
          console.error('[MapService] Error cargando estilo vectorial:', err);
          alert('Error cargando estilo de mapa base. Volviendo al mapa anterior.');
          if (previousLayer && this.baseLayer === layer) this._swapBaseLayer(previousLayer);
        });
    }
    return layer;
  }

  _swapBaseLayer(newLayer) {
    if (this.baseLayer && this.baseLayer !== newLayer) {
      this.map.removeLayer(this.baseLayer);
    }
    this.baseLayer = newLayer;
    this.map.getLayers().insertAt(0, newLayer);
  }

  _attachTileErrorRollback(newLayer, previousLayer, previousOverlay) {
    const source = newLayer.getSource();
    if (!source || typeof source.on !== 'function') return;
    let handled = false;
    source.on('tileloaderror', () => {
      if (handled) return;
      handled = true;
      console.error('[MapService] tileloaderror en capa base, rollback');
      alert('Error cargando mapa base. Volviendo al mapa anterior.');
      if (previousLayer && this.baseLayer === newLayer) {
        this._swapBaseLayer(previousLayer);
        this._restoreOverlay(previousOverlay);
      }
    });
  }

  _applyOverlay(config) {
    if (this._baseOverlay) {
      this.map.removeLayer(this._baseOverlay);
      this._baseOverlay = null;
    }
    if (config && typeof config === 'object' && config.overlayUrl) {
      const overlay = new TileLayer({
        source: new XYZ({ url: config.overlayUrl }),
        zIndex: 5,
      });
      this.map.addLayer(overlay);
      this._baseOverlay = overlay;
    }
  }

  _restoreOverlay(previousOverlay) {
    if (this._baseOverlay) {
      this.map.removeLayer(this._baseOverlay);
      this._baseOverlay = null;
    }
    if (previousOverlay) {
      this.map.addLayer(previousOverlay);
      this._baseOverlay = previousOverlay;
    }
  }

  /**
   * Retorna la capa base actual
   */
  getBaseLayer() {
    return this.baseLayer;
  }

  destroy() {
    if (this.map) {
      this.map.setTarget(null)
      this.map = null
    }

    this.baseLayer = null
  }

  getActiveLayers() {
    return this.map.getLayers().getArray();
  }

  layerExists(layerId) {
    if (!this.map) return false;
    const layers = this.map.getLayers().getArray();
    const exists = layers.some(layer => layer.get("id") === layerId);
    return exists;
  }


  addLayerToMap(layer, filterGeometry = null) {
    if (!this.map) return;
    if (this.layerExists(layer.get("id"))) return;

    if (filterGeometry) {
      this.applyLayerClipping(layer, filterGeometry);
    }

    this.map.addLayer(layer);
  }

  applyLayerClipping(layer, filterGeometry) {
    const geometryType = filterGeometry.type;
    const coordinates = filterGeometry.coordinates;

    layer.on('prerender', (e) => {
      const ctx = e.context;
      const frameState = e.frameState;
      const pixelRatio = frameState.pixelRatio;

      ctx.save();
      ctx.beginPath();

      const drawRing = (ring) => {
        ring.forEach((coord, i) => {
          const pixel = this.map.getPixelFromCoordinate(coord);
          // Handle cases where pixel might be null (though getPixelFromCoordinate usually returns valid [x,y])
          if (!pixel) return;

          const x = pixel[0] * pixelRatio;
          const y = pixel[1] * pixelRatio;

          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
      };

      try {
        if (geometryType === 'Polygon') {
          // GeoJSON Polygon coordinates: [outerRing, hole1, hole2...]
          // OpenLayers simple geometry usually expects standard array structure
          // We iterate over rings
          coordinates.forEach(ring => drawRing(ring));
        } else if (geometryType === 'MultiPolygon') {
          // MultiPolygon: [ [rings], [rings] ]
          coordinates.forEach(polygon => {
            polygon.forEach(ring => drawRing(ring));
          });
        }
      } catch (err) {
        console.error("Error applying clip", err);
      }

      ctx.clip();
    });

    layer.on('postrender', (e) => {
      const ctx = e.context;
      ctx.restore();
    });
  }

  removeLayerFromMapByID(layerID) {
    if (!this.map) return;
    console.log("Removiendo capa por ID:", layerID);

    const layersToRemove = [];
    this.map.getLayers().forEach(layer => {
      if (layer && layer.get("id") === layerID) {
        layersToRemove.push(layer);
      }
    });

    layersToRemove.forEach(layer => {
      this.map.removeLayer(layer);
    });
  }

  /**
   * Obtiene el extent (bounding box) actual del mapa
   * Returns: [xMin, yMin, xMax, yMax] en EPSG:3857
   */
  getExtent() {
    if (!this.map) return null;
    return this.map.getView().calculateExtent(this.map.getSize());
  }

  /**
   * Hace zoom a un extent específico
   */
  zoomToExtent(extent) {
    if (!this.map) return;

    this.map.getView().fit(extent, {
      duration: 1000,
    });
  }

  /**
   * Añade una capa operativa (WMS o ArcGIS) al mapa
   * @param {Object} layerConfig Configuración de la capa desde el backend/store
   */
  addLayer(layerConfig) {
    if (!this.map) return;
    if (this.layerExists(layerConfig.id)) {
      console.log(`[MapService] La capa ${layerConfig.name} ya existe.`);
      return;
    }

    console.log(`[MapService] Añadiendo capa:`, layerConfig);
    let olLayer = null;

    try {
      if (layerConfig.provider.name === "geoserver") {
        // Configuración WMS (GeoServer)
        const geoserverData = layerConfig.provider.geoserverdata;
        // Usar filename o datastore como nombre de capa WMS
        const layerName = geoserverData.filename || geoserverData.datastore;
        const layersParam = `${geoserverData.workspace}:${layerName}`;

        const source = new TileWMS({
          url: layerConfig.provider.url,
          params: {
            'LAYERS': layersParam,
            'TILED': true,
            'VERSION': '1.1.1',
          },
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
        });

        olLayer = new TileLayer({
          source: source,
          opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
          zIndex: 10 // Capas operativas sobre el mapa base
        });

      } else if (layerConfig.provider.name === "arcgis") {
        // Configuración ArcGIS
        // Nota: Soportar FeatureServer vs MapServer si es necesario.
        // Por ahora asumimos MapServer (ImageArcGISRest) para simplicidad y compatibilidad con visor clásico
        // Si tiene FeatureServer, se manejaría diferente (VectorLayer), pero empezamos con esto.

        const source = new ImageArcGISRest({
          ratio: 1,
          params: {},
          url: layerConfig.provider.url
        });

        olLayer = new ImageLayer({
          source: source,
          opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
          zIndex: 10
        });
      }

      if (olLayer) {
        // Asignar ID y metadatos a la capa OL para poder buscarla después
        olLayer.set('id', layerConfig.id);
        olLayer.set('name', layerConfig.name);

        this.map.addLayer(olLayer);
        console.log(`[MapService] Capa añadida exitosamente.`);
      }

    } catch (error) {
      console.error(`[MapService] Error añadiendo capa ${layerConfig.name}:`, error);
    }
  }

  /**
   * Remueve una capa del mapa por su ID
   * @param {String|Number} layerId 
   */
  removeLayer(layerId) {
    if (!this.map) return;

    // Buscar la capa en el arreglo de capas del mapa
    const layersToRemove = [];
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        layersToRemove.push(layer);
      }
    });

    layersToRemove.forEach(layer => {
      this.map.removeLayer(layer);
      console.log(`[MapService] Capa removida: ${layerId}`);
    });
  }

  /**
   * Ajusta la opacidad de una capa
   * @param {String|Number} layerId 
   * @param {Number} opacity (0 a 1)
   */
  setLayerOpacity(layerId, opacity) {
    if (!this.map) return;

    let layerFound = false;
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        layer.setOpacity(opacity);
        layerFound = true;
      }
    });

    if (!layerFound) {
      console.warn(`[MapService] No se encontró la capa ${layerId} para cambiar opacidad.`);
    }
  }

  /**
   * Applies filter params to the matching OL layer's WMS/ArcGIS source.
   * Follows the same iteration pattern as setLayerOpacity().
   * Raster layers are explicitly skipped (excluded from Phase 3 scope).
   * TODO: add raster CQL_FILTER support in a future phase.
   *
   * @param {Number|String} layerId
   * @param {Object} params  { cqlFilter?: String|null, layerDefs?: String|null }
   */
  updateLayerFilter(layerId, params = {}) {
    if (!this.map) return;
    const { cqlFilter, layerDefs } = params;
    let found = false;

    this.map.getLayers().forEach(olLayer => {
      if (olLayer.get('id') !== layerId) return;

      if (olLayer.get('layerType') === 'raster') {
        // TODO: raster CQL support — excluded from Phase 3 scope
        return;
      }

      const source = olLayer.getSource();
      if (!source || typeof source.updateParams !== 'function') return;

      if (cqlFilter !== undefined) {
        source.updateParams({ CQL_FILTER: cqlFilter });
      }
      if (layerDefs !== undefined) {
        source.updateParams({ LAYERDEFS: layerDefs });
      }
      found = true;
    });

    if (!found) {
      console.warn(`[MapService] updateLayerFilter: layer ${layerId} not found or skipped.`);
    }
  }

  /**
   * Returns the current filter params active on an OL layer's source.
   * Used by selectService to build CQL-aware GetFeatureInfo requests.
   *
   * @param {Number|String} layerId
   * @returns {{ cqlFilter: String|null, layerDefs: String|null } | null}
   */
  getLayerActiveFilter(layerId) {
    if (!this.map) return null;
    let result = null;

    this.map.getLayers().forEach(olLayer => {
      if (olLayer.get('id') !== layerId) return;
      const source = olLayer.getSource();
      if (!source || typeof source.getParams !== 'function') return;
      const p = source.getParams();
      result = {
        cqlFilter: p.CQL_FILTER || null,
        layerDefs:  p.LAYERDEFS  || null,
      };
    });

    return result;
  }

}

export const mapService = new MapService();
